import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { interval, Observable, Subscription } from 'rxjs';
import moment, { Duration, Moment } from 'moment';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'workitem-simple-timer',
  templateUrl: './workitem-simple-timer.component.html',
  styleUrls: ['./workitem-simple-timer.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class WorkitemSimpleTimerComponent implements OnInit, OnDestroy, OnChanges {
  private static readonly INTERVAL = interval(1000);

  @Input()
  public initialTime: string = null;

  @Input()
  public showTimer: boolean = false;

  public timerText: string = '';

  @Input()
  public showRemainingSeconds: boolean = false;

  @Input()
  public remainingSeconds: number = 0;

  private _timerSubscription: Subscription;
  private _dateStringsSubscription: Subscription;
  private _interval: Observable<number>;

  private _startTime: Moment;
  private _durationFormatter: (duration: Duration) => string = () => '';

  @Output()
  public onTimerTextChanged: EventEmitter<string> = new EventEmitter<string>();

  constructor(private _translateService: TranslateService) {
    this._interval = WorkitemSimpleTimerComponent.INTERVAL;
  }

  ngOnInit(): void {
    this.createDurationFormatter();
    this.startOrStopTimer();
  }

  ngOnDestroy(): void {
    this._stopTimer();

    if (this._dateStringsSubscription) {
      this._dateStringsSubscription.unsubscribe();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.showTimer) {
      this.startOrStopTimer();
    }
    if (changes.initialTime) {
      this._startTime = this._getInitialTime();
    }
  }

  public startOrStopTimer(): void {
    if (this.showTimer) {
      this._startTimer();
    } else {
      this._stopTimer();
    }
  }

  private createDurationFormatter(): void {
    this._dateStringsSubscription = this._translateService.get('workitemSimpleTimer').subscribe(dateStrings => {
      this._durationFormatter = (duration) => {
        if (duration.asMinutes() < 1) {
          return '';
        }

        if (duration.asHours() < 1) {
          return `${duration.minutes()}${dateStrings.minutes}`;
        }

        if (duration.asHours() < 10) {
          return `${duration.hours()}${dateStrings.hours}${duration.minutes()}${dateStrings.minutes}`;
        }

        if (duration.asHours() < 48) {
          const hours = Math.ceil(duration.asHours());
          return `${hours}${dateStrings.hours}`;
        }

        const days = Math.ceil(duration.asDays());
        return `${days}${dateStrings.days}`;
      };
    });
  }

  private _startTimer(): void {
    if (this._timerSubscription) {
      return;
    }

    this._timerSubscription = this._interval.subscribe(() => {
      this._timerTick();
    });
  }

  private _stopTimer(): void {
    if (this._timerSubscription) {
      this._timerSubscription.unsubscribe();
    }
    this._timerSubscription = null;
  }

  private _getInitialTime(): Moment {
    return this.initialTime ? moment(this.initialTime) : moment();
  }

  private _timerTick(): void {
    this.timerText = this._getTimerText();
  }

  private _getTimerText(): string {
    const duration = moment.duration(moment().diff(this._startTime));
    const formattedDuration = this._durationFormatter(duration);
    this.onTimerTextChanged.emit(formattedDuration);
    return formattedDuration;
  }
}
